import {
  progressMonitorWordsV2Kind,
  shouldShowGradeLevelAssessmentV2CanRetell,
  shouldShowGradeLevelFluencyCanRetell,
} from '@/components/feedbacks/session-feedback-utils';
import { booleanToCustomString } from '@/lib/string-utils';
import { AssessmentContent, AssessmentResult, isInAppAssessmentFormat } from '@airreading/common/data';
import { ReactNode } from 'react';

function ReadOnlyValue({ testId, children }: { testId?: string; children: ReactNode }) {
  if (!testId) {
    return <>{children}</>;
  }
  return <div data-testid={testId}>{children}</div>;
}

function ReadOnlyLabelValue({ label, value, testId }: { label: ReactNode; value: ReactNode; testId?: string }) {
  return (
    <div>
      <div className="font-bold">{label}</div>
      <ReadOnlyValue testId={testId}>{value ?? '-'}</ReadOnlyValue>
    </div>
  );
}

export const ReadOnlyAssessmentResultForm = ({
  result,
  sortedAssessmentContents,
  resultIndex,
  testId,
  nestedValueTestId,
  studentGrade,
}: {
  result: AssessmentResult;
  resultIndex: number;
  sortedAssessmentContents: AssessmentContent[];
  assessmentDisplayName: string;
  testId?: string;
  nestedValueTestId?: (field: string) => string | undefined;
  studentGrade?: string | null;
}) => {
  const showGradeLevelFluencyCanRetell = shouldShowGradeLevelFluencyCanRetell(studentGrade);
  const showGradeLevelAssessmentV2CanRetell = shouldShowGradeLevelAssessmentV2CanRetell(studentGrade);
  const content = sortedAssessmentContents.find((c) => c.id === result.assessmentContentId);
  if (!content) {
    return <p>Assessment content {result.assessmentContentId} not found</p>;
  }

  const {
    assessmentFinished,
    incorrectWords,
    incorrectWordsCount,
    incorrectWordsReadFromDiagnosticPassage,
    phonemicReadingResult,
    comprehensionSkillResult,
    comprehensionPassageResult,
    fluencySkillResult,
    totalWordsRead,
    canRetell,
    incorrectlWordsReadFromPMList,
    finishedEarly,
    totalSecondsSpent,
  } = result;
  const { format, readingPassage, words } = content;

  if (format === 'diagnostic_word_list') {
    if (incorrectWordsCount == null) {
      return null;
    }

    const currContentWords = sortedAssessmentContents[resultIndex].words;
    const hasPassageResults = totalWordsRead != null || incorrectWordsReadFromDiagnosticPassage.length > 0;

    if (incorrectWords.length === 0 && !hasPassageResults) {
      return <ReadOnlyValue testId={testId}>All correct</ReadOnlyValue>;
    }

    return (
      <ReadOnlyValue testId={testId}>
        <div className="flex flex-col gap-2">
          {incorrectWords.length === 0 ? (
            <span>All correct</span>
          ) : (
            <>
              <p>Missed word(s)</p>
              <span>
                {incorrectWords
                  .toSorted(
                    (a, b) => currContentWords.findIndex((w) => w === a) - currContentWords.findIndex((w) => w === b),
                  )
                  .join(', ')}
              </span>
            </>
          )}
          {hasPassageResults && (
            <div className="flex flex-wrap gap-8 mt-2">
              <ReadOnlyLabelValue
                label="Number of words read"
                value={totalWordsRead}
                testId={nestedValueTestId?.('total-words-read')}
              />
              <ReadOnlyLabelValue
                label="Number of mistakes"
                value={incorrectWordsReadFromDiagnosticPassage.length}
                testId={nestedValueTestId?.('incorrect-words-from-diagnostic-passage')}
              />
            </div>
          )}
        </div>
      </ReadOnlyValue>
    );
  }

  if (format === 'mastery_check_reading_fluency_passage' || format === 'mastery_check_reading_fluency_passage_v2') {
    return <ReadOnlyValue testId={testId}>{fluencySkillResult ?? '-'}</ReadOnlyValue>;
  }

  if (format === 'mastery_check_phonemic_reading') {
    return <ReadOnlyValue testId={testId}>{booleanToCustomString(phonemicReadingResult)}</ReadOnlyValue>;
  }

  if (format === 'mastery_check_comprehesion_passage') {
    return <ReadOnlyValue testId={testId}>{booleanToCustomString(comprehensionPassageResult)}</ReadOnlyValue>;
  }

  if (format === 'mastery_check_reading' || format === 'mastery_check_skill_preview') {
    if (incorrectWordsCount == null) {
      return null;
    }
    return <ReadOnlyValue testId={testId}>{`Missed ${incorrectWordsCount} word(s)`}</ReadOnlyValue>;
  }

  if (format === 'mastery_check_comprehesion_skill') {
    return <ReadOnlyValue testId={testId}>{comprehensionSkillResult ?? '-'}</ReadOnlyValue>;
  }

  if (format === 'grade_level_fluency') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        {showGradeLevelFluencyCanRetell && (
          <ReadOnlyLabelValue
            label="Is the student able to retell?"
            value={booleanToCustomString(canRetell)}
            testId={nestedValueTestId?.('can-retell')}
          />
        )}
      </div>
    );
  }

  if (format === 'progress_monitor_sounds_v2') {
    const kind = progressMonitorWordsV2Kind({ readingPassage, words });

    if (kind === 'passage') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Letter sounds</div>
          <div className="flex flex-wrap gap-8 w-full">
            <ReadOnlyLabelValue
              label="Number of letter sounds read"
              value={totalWordsRead}
              testId={nestedValueTestId?.('total-words-read')}
            />
            <ReadOnlyLabelValue
              label="Number of mistakes"
              value={incorrectWordsCount}
              testId={nestedValueTestId?.('incorrect-words-count')}
            />
          </div>
        </div>
      );
    }

    if (kind === 'word-list') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Word list</div>
          <div className="flex flex-wrap gap-8 w-full">
            <ReadOnlyLabelValue
              label="Number of words read from the list"
              value={totalWordsRead}
              testId={nestedValueTestId?.('total-words-read')}
            />
            <ReadOnlyLabelValue
              label="Number of words read incorrectly from the list"
              value={incorrectWordsCount}
              testId={nestedValueTestId?.('incorrect-words-count')}
            />
            <ReadOnlyLabelValue
              label="Student finished reading early"
              value={booleanToCustomString(finishedEarly)}
              testId={nestedValueTestId?.('finished-early')}
            />
            {finishedEarly && (
              <ReadOnlyLabelValue
                label={
                  <>
                    How many seconds did the student use in <strong>total</strong>?
                  </>
                }
                value={totalSecondsSpent ?? 'N/A'}
                testId={nestedValueTestId?.('total-seconds-spent')}
              />
            )}
          </div>
        </div>
      );
    }

    return null;
  }

  if (format === 'progress_monitor_words_v2') {
    const kind = progressMonitorWordsV2Kind({ readingPassage, words });
    const finishedEarlyFields = (
      <>
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        {finishedEarly && (
          <ReadOnlyLabelValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
            testId={nestedValueTestId?.('total-seconds-spent')}
          />
        )}
      </>
    );

    if (kind === 'passage') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Passage</div>
          <div className="flex flex-wrap gap-8 w-full">
            <ReadOnlyLabelValue
              label="Number of words read"
              value={totalWordsRead}
              testId={nestedValueTestId?.('total-words-read')}
            />
            <ReadOnlyLabelValue
              label="Number of mistakes"
              value={incorrectWordsCount}
              testId={nestedValueTestId?.('incorrect-words-count')}
            />
            {finishedEarlyFields}
            <ReadOnlyLabelValue
              label="Is the student able to retell?"
              value={booleanToCustomString(canRetell)}
              testId={nestedValueTestId?.('can-retell')}
            />
          </div>
        </div>
      );
    }

    if (kind === 'word-list') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Word list</div>
          <div className="flex flex-wrap gap-8 w-full">
            <ReadOnlyLabelValue
              label="Number of words read from the list"
              value={totalWordsRead}
              testId={nestedValueTestId?.('total-words-read')}
            />
            <ReadOnlyLabelValue
              label="Number of words read incorrectly from the list"
              value={incorrectWordsCount}
              testId={nestedValueTestId?.('incorrect-words-count')}
            />
            {finishedEarlyFields}
          </div>
        </div>
      );
    }

    return null;
  }

  if (format === 'progress_monitor_letter_names_v2') {
    const kind = progressMonitorWordsV2Kind({ readingPassage, words });

    if (kind === 'passage') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Letter names</div>
          <div className="flex flex-wrap gap-8 w-full">
            <ReadOnlyLabelValue
              label="Number of letter names read"
              value={totalWordsRead}
              testId={nestedValueTestId?.('total-words-read')}
            />
            <ReadOnlyLabelValue
              label="Number of mistakes"
              value={incorrectWordsCount}
              testId={nestedValueTestId?.('incorrect-words-count')}
            />
            <ReadOnlyLabelValue
              label="Student finished reading early"
              value={booleanToCustomString(finishedEarly)}
              testId={nestedValueTestId?.('finished-early')}
            />
            {finishedEarly && (
              <ReadOnlyLabelValue
                label={
                  <>
                    How many seconds did the student use in <strong>total</strong>?
                  </>
                }
                value={totalSecondsSpent ?? 'N/A'}
                testId={nestedValueTestId?.('total-seconds-spent')}
              />
            )}
          </div>
        </div>
      );
    }

    if (kind === 'word-list') {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="font-bold">Word list</div>
          <ReadOnlyLabelValue
            label="Number of words read incorrectly from the list"
            value={incorrectWordsCount == null ? null : `Missed ${incorrectWordsCount}`}
            testId={nestedValueTestId?.('incorrect-words-count')}
          />
        </div>
      );
    }

    return null;
  }

  if (format === 'grade_level_fluency_v2') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        {finishedEarly && (
          <ReadOnlyLabelValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
            testId={nestedValueTestId?.('total-seconds-spent')}
          />
        )}
        {showGradeLevelAssessmentV2CanRetell && (
          <ReadOnlyLabelValue
            label="Is the student able to retell?"
            value={booleanToCustomString(canRetell)}
            testId={nestedValueTestId?.('can-retell')}
          />
        )}
      </div>
    );
  }

  if (format === 'fluency_check') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        {finishedEarly && (
          <ReadOnlyLabelValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
            testId={nestedValueTestId?.('total-seconds-spent')}
          />
        )}
      </div>
    );
  }

  if (isInAppAssessmentFormat(format)) {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Incorrect words"
          value={incorrectWords.length === 0 ? null : incorrectWords.join(', ')}
          testId={nestedValueTestId?.('incorrect-words')}
        />
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        <ReadOnlyLabelValue
          label={
            <>
              How many seconds did the student use in <strong>total</strong>?
            </>
          }
          value={totalSecondsSpent}
          testId={nestedValueTestId?.('total-seconds-spent')}
        />
        <ReadOnlyLabelValue
          label="Assessment finished"
          value={booleanToCustomString(assessmentFinished)}
          testId={nestedValueTestId?.('assessment-finished')}
        />
      </div>
    );
  }

  if (format === 'progress_monitor_sounds') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of letter sounds read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Number of words read incorrectly from the list"
          value={incorrectlWordsReadFromPMList}
          testId={nestedValueTestId?.('incorrect-words-from-list')}
        />
      </div>
    );
  }

  if (format === 'progress_monitor_letter_names') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of letter names read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Number of words read incorrectly from the list"
          value={incorrectlWordsReadFromPMList == null ? null : `Missed ${incorrectlWordsReadFromPMList}`}
          testId={nestedValueTestId?.('incorrect-words-from-list')}
        />
      </div>
    );
  }

  if (format === 'progress_monitor_words') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Number of words read incorrectly from the list"
          value={incorrectlWordsReadFromPMList}
          testId={nestedValueTestId?.('incorrect-words-from-list')}
        />
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        {finishedEarly && (
          <ReadOnlyLabelValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
            testId={nestedValueTestId?.('total-seconds-spent')}
          />
        )}
        <ReadOnlyLabelValue
          label="Is the student able to retell?"
          value={booleanToCustomString(canRetell)}
          testId={nestedValueTestId?.('can-retell')}
        />
      </div>
    );
  }

  if (format === 'progress_monitor_text') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <ReadOnlyLabelValue
          label="Number of words read"
          value={totalWordsRead}
          testId={nestedValueTestId?.('total-words-read')}
        />
        <ReadOnlyLabelValue
          label="Number of mistakes"
          value={incorrectWordsCount}
          testId={nestedValueTestId?.('incorrect-words-count')}
        />
        <ReadOnlyLabelValue
          label="Student finished reading early"
          value={booleanToCustomString(finishedEarly)}
          testId={nestedValueTestId?.('finished-early')}
        />
        {finishedEarly && (
          <ReadOnlyLabelValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
            testId={nestedValueTestId?.('total-seconds-spent')}
          />
        )}
      </div>
    );
  }

  return <ReadOnlyValue testId={testId}>{content.format} display not implemented</ReadOnlyValue>;
};
